/**
 * Champs de formulaire numériques — conversions pures, sans React.
 *
 * ⚠️ Les valeurs sont conservées en **chaînes** tant qu'un formulaire est
 * ouvert. Un champ doit pouvoir être vidé ou contenir « 12. » le temps d'une
 * frappe ; convertir à chaque touche ferait bagarrer l'état avec la saisie. La
 * conversion n'a lieu qu'à l'enregistrement.
 *
 * ⚠️ Aucun calcul de macros ici — le serveur en est seul juge (§5.1). Ce fichier
 * ne fait que traduire entre l'affichage et les nombres.
 *
 * Extrait de `screens/library/templateDraft.ts` à l'étape 6 : l'éditeur
 * d'aliment a exactement les mêmes besoins que l'éditeur de modèle, et
 * dupliquer six fonctions de conversion aurait garanti qu'elles finissent par
 * diverger.
 */

import type { MacroSet } from '../../../shared/types';

/** Les quatre macros, telles qu'un formulaire les manipule : en chaînes. */
export interface MacroFields {
  protein: string;
  carbs: string;
  fat: string;
  kcal: string;
}

export const EMPTY_MACROS: MacroFields = { protein: '', carbs: '', fat: '', kcal: '' };

/**
 * Chaîne → nombre, tolérante : un champ vide vaut zéro, pas `NaN`.
 *
 * ⚠️ La **virgule** est acceptée. `NumberField` la remplace déjà à la frappe,
 * mais une valeur peut aussi arriver d'ailleurs — d'un brouillon pré-rempli,
 * par exemple. Sans cette tolérance, `Number('6,3')` vaut `NaN` et la macro
 * tomberait silencieusement à zéro.
 */
export function toNumber(raw: string): number {
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed === '') return 0;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Nombre → champ : zéro s'affiche vide, pour ne pas gêner la saisie.
 *
 * ⚠️ Décimales en **virgule**, comme partout ailleurs dans l'app. Une fiche
 * pré-remplie qui afficherait « 6.3 » à côté d'un aperçu affichant « 6,3 »
 * mélangerait deux conventions sur le même écran — constaté en relisant le
 * rendu de l'écran de quantité d'un produit Open Food Facts.
 */
export function toField(value: number): string {
  return value === 0 ? '' : String(value).replace('.', ',');
}

export function macrosOf(fields: MacroFields): MacroSet {
  return {
    protein: toNumber(fields.protein),
    carbs: toNumber(fields.carbs),
    fat: toNumber(fields.fat),
    kcal: toNumber(fields.kcal),
  };
}

export function fieldsOf(macros: MacroSet): MacroFields {
  return {
    protein: toField(macros.protein),
    carbs: toField(macros.carbs),
    fat: toField(macros.fat),
    kcal: toField(macros.kcal),
  };
}

/** Écart 4/4/9 en kcal entières. Indicatif (§5.3) — ne sert qu'à afficher. */
export function gapOf(macros: MacroSet): { theoretical: number; delta: number } {
  const theoretical = macros.protein * 4 + macros.carbs * 4 + macros.fat * 9;
  return { theoretical: Math.round(theoretical), delta: Math.round(macros.kcal - theoretical) };
}
